

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert,
    Switch,
    FormControlLabel
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";
import Autocomplete from '@mui/material/Autocomplete';
import { CountrySelect } from 'components/GeographySelect';
import { useCurrencyCodes } from 'hooks/useCurrencyCodes';

const NewMerchantModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const { toDropdownOptions } = useCurrencyCodes();
    const currencyOptions = toDropdownOptions({ labelFormat: 'codeName' });
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        merchant_type: '',
        website: '',
        payment_currency: '',
        shipping_address: '',
        email: '',
        phone: '',
        billing_address: '',
        payment_term: 'NET 30',
        is_partner: false,
        operating_country: '',
        tax_id: '',
        tax_rate: '',
        account_id: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [accounts, setAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchAccounts();
        }
    }, [isOpen]);

    const fetchAccounts = async () => {
        setLoadingAccounts(true);
        try {
            const response = await axiosInstance.get('/accounts/?dropdown=true');
            setAccounts(response.data || []);
        } catch (error) {
            console.error('Error fetching accounts:', error);
        } finally {
            setLoadingAccounts(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Merchant Name is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                account_id: formData.account_id ? parseInt(formData.account_id) : null,
                tax_rate: formData.tax_rate ? parseFloat(formData.tax_rate) : null,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
        } catch (error) {
            console.error('Error saving Merchant:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Merchant',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '900px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Merchant</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Merchant Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Merchant Type</InputLabel>
                                        <Select
                                            name="merchant_type"
                                            value={formData.merchant_type}
                                            onChange={handleInputChange}
                                            label="Merchant Type"

                                        >
                                            <MenuItem value=""><em>Select Type</em></MenuItem>
                                            <MenuItem value="Accommodation">Accommodation</MenuItem>
                                            <MenuItem value="Activity">Activity</MenuItem>
                                            <MenuItem value="Transportation">Transportation</MenuItem>
                                            <MenuItem value="Meals">Meals</MenuItem>
                                            <MenuItem value="Misc.">Misc.</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Website"
                                        name="website"
                                        value={formData.website}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={currencyOptions}
                                        getOptionLabel={(opt) => opt?.label || opt?.value || ''}
                                        isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                        value={
                                            currencyOptions.find(
                                                (c) => c.value === formData.payment_currency
                                            ) || null
                                        }
                                        onChange={(_, next) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                payment_currency: next?.value || '',
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Payment Currency"
                                                variant="outlined"
                                                margin="normal"
                                                fullWidth
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Payment Term</InputLabel>
                                        <Select
                                            name="payment_term"
                                            value={formData.payment_term}
                                            onChange={handleInputChange}
                                            label="Payment Term"

                                        >
                                            <MenuItem value="NET 7">NET 7</MenuItem>
                                            <MenuItem value="NET 15">NET 15</MenuItem>
                                            <MenuItem value="NET 30">NET 30</MenuItem>
                                            <MenuItem value="NET 45">NET 45</MenuItem>
                                            <MenuItem value="NET 60">NET 60</MenuItem>
                                            <MenuItem value="NET 90">NET 90</MenuItem>
                                            <MenuItem value="Due on Receipt">Due on Receipt</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <MDBox mt={2}>
                                        <CountrySelect
                                            label="Operating Country"
                                            value={formData.operating_country || ''}
                                            size="small"
                                            onChange={(nextCountry) => {
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    operating_country: nextCountry || '',
                                                }));
                                            }}
                                        />
                                    </MDBox>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Tax ID"
                                        name="tax_id"
                                        value={formData.tax_id}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Tax Rate (%)"
                                        name="tax_rate"
                                        type="number"
                                        value={formData.tax_rate}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Account</InputLabel>
                                        <Select
                                            name="account_id"
                                            value={formData.account_id}
                                            onChange={handleInputChange}
                                            label="Account"

                                            disabled={loadingAccounts}
                                        >
                                            <MenuItem value=""><em>Select an Account</em></MenuItem>
                                            {accounts.map(acc => (
                                                <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={<Switch checked={formData.is_partner} name="is_partner" onChange={handleInputChange} />}
                                        label="Partner"
                                        sx={{ mt: 2 }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Shipping Address"
                                        name="shipping_address"
                                        multiline
                                        rows={2}
                                        value={formData.shipping_address}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Billing Address"
                                        name="billing_address"
                                        multiline
                                        rows={2}
                                        value={formData.billing_address}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Merchant'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewMerchantModal;

